import { useQuery } from '@tanstack/react-query'
import api, { deploymentApi, ticketApi } from '../../lib/api'
import type { PaginatedResponse } from '../../lib/api'
import type { MerchantEquipmentAsset } from './merchantFormatters'

export interface Merchant {
  id: string
  application_id: string | null
  dba_name: string
  legal_name: string | null
  business_email: string | null
  business_phone: string | null
  status: string
  fiserv_mid: string | null
  fiserv_north_number?: string | null
  monthly_volume: number | null
  ytd_volume: number | null
  chargeback_ratio: number | null
  last_batch_at: string | null
  last_transaction_sync_at: string | null
  last_transaction_sync_status: 'success' | 'failed' | 'skipped' | null
  last_transaction_sync_error: string | null
  first_batch_at: string | null
  risk_tier: string
  open_chargebacks_count?: number
  account_groups?: { id: string; name: string }[]
  created_at: string
}

export interface MerchantFilters {
  page?: number
  per_page?: number
  merchant?: string
  search?: string
  account_group?: string
  status?: string
  no_batch_7_days?: boolean
  has_open_chargebacks?: boolean
  first_batch_this_week?: boolean
  sync_stale?: boolean
  sync_failed?: boolean
  sort_by?: string
  sort_dir?: 'asc' | 'desc'
}

export interface Transaction {
  id: string
  fiserv_txn_id: string
  txn_date: string
  card_type: string | null
  txn_type: string
  amount: string
  status: string
}

export interface ChargebackRecord {
  id: string
  fiserv_case_id: string
  case_type: string
  amount: string
  reason_code: string | null
  card_type: string | null
  response_due_at: string | null
  status: string
}

export interface MerchantNmiStatus {
  gateway_id: string
  status: string
  message?: string
}

export interface AccountContextLead {
  id: string
  dba_name: string | null
  legal_name: string | null
  business_email: string | null
  contact_name: string | null
  stage: string
  merchant_id: string | null
  assigned_to: { id: string; name: string } | null
  assigned_to_secondary: { id: string; name: string } | null
}

export interface MerchantAccountContext {
  merchant: Merchant | null
  application: {
    id: string
    fiserv_status: string | null
    fiserv_as_ref_id: string | null
    pricing_option: string | null
    submitted_at: string | null
  } | null
  lead: AccountContextLead | null
}

export function useMerchants(filters: MerchantFilters) {
  return useQuery({
    queryKey: ['merchants', filters],
    queryFn: async () => {
      const resp = await api.get<PaginatedResponse<Merchant>>('/merchants', { params: filters })
      return resp.data
    },
  })
}

export function useMerchantContext(merchantId: string | null) {
  return useQuery({
    queryKey: ['merchant-context', merchantId],
    queryFn: async () => {
      const resp = await api.get<MerchantAccountContext>(`/merchants/${merchantId}/context`)
      return resp.data
    },
    enabled: !!merchantId,
  })
}

export function useLeadContext(leadId: string | null) {
  return useQuery({
    queryKey: ['lead-context', leadId],
    queryFn: async () => {
      const resp = await api.get<MerchantAccountContext>(`/leads/${leadId}/context`)
      return resp.data
    },
    enabled: !!leadId,
  })
}

export function useMerchantTransactions(merchantId: string | null) {
  return useQuery({
    queryKey: ['merchant-transactions', merchantId],
    queryFn: async () => {
      const resp = await api.get<PaginatedResponse<Transaction>>(`/merchants/${merchantId}/transactions`)
      return resp.data
    },
    enabled: !!merchantId,
  })
}

export function useMerchantChargebacks(merchantId: string | null) {
  return useQuery({
    queryKey: ['merchant-chargebacks', merchantId],
    queryFn: async () => {
      const resp = await api.get<PaginatedResponse<ChargebackRecord>>(`/merchants/${merchantId}/chargebacks`)
      return resp.data
    },
    enabled: !!merchantId,
  })
}

export function useMerchantTickets(merchantId: string | null) {
  return useQuery({
    queryKey: ['merchant-tickets', merchantId],
    queryFn: () => ticketApi.listForMerchant(merchantId ?? ''),
    enabled: !!merchantId,
  })
}

export function useMerchantDeploymentTasks(merchantId: string | null) {
  return useQuery({
    queryKey: ['merchant-deployment-tasks', merchantId],
    queryFn: () => deploymentApi.list({
      merchant_id: merchantId ?? '',
      per_page: '100',
    }),
    enabled: !!merchantId,
    refetchInterval: 15_000,
  })
}

export function useMerchantNmiStatus(merchantId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ['merchant-nmi-status', merchantId],
    queryFn: async () => {
      const resp = await api.get<MerchantNmiStatus>(`/merchants/${merchantId}/nmi/status`)
      return resp.data
    },
    enabled: !!merchantId && enabled,
  })
}

export function useMerchantEquipmentAssets(merchantId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ['merchant-equipment-assets', merchantId],
    queryFn: async () => {
      const resp = await api.get<{ data: MerchantEquipmentAsset[] }>(`/merchants/${merchantId}/equipment-assets`)
      return resp.data.data
    },
    enabled: !!merchantId && enabled,
  })
}
